import { useEffect } from "react";
import { Outlet, useSearchParams, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { Breadcrumbs } from "./Breadcrumbs";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu, Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSubscription } from "@/hooks/useSubscription";
import { useBusinessId } from "@/hooks/useBusinessId";
import SubscriptionGuard from "@/components/SubscriptionGuard";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type OnboardingState = "loading" | "needs-onboarding" | "done";

/**
 * Checks whether the current user's business has completed onboarding. Uses
 * React Query so route changes inside /dashboard don't re-fire the queries
 * — onboarded_at flips at most once per business and a 30-min cache is plenty.
 */
function useOnboardingCheck(): OnboardingState {
  const { user } = useAuth();
  const businessId = useBusinessId(user);

  const query = useQuery({
    queryKey: ["onboarding-check", businessId],
    enabled: !!businessId,
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<{ onboarded: boolean }> => {
      const { data } = await supabase
        .from("businesses")
        .select("onboarded_at")
        .eq("id", businessId!)
        .maybeSingle();
      return { onboarded: !!data?.onboarded_at };
    },
  });

  if (!user) return "loading";
  // No business yet — let DashboardLayout handle it (paywall, etc.).
  if (user && businessId === null && !query.isLoading) return "done";
  if (query.isLoading || !query.data) return "loading";
  return query.data.onboarded ? "done" : "needs-onboarding";
}

/* Trial banner removed: checkout captures the card upfront
   (`payment_method_collection: "always"` in _shared/checkout.ts), so every
   trialing user already has a payment method and the "add one to keep access"
   CTA was asking for something already done. Trial end date lives on Billing;
   a failed card is handled by the dunning redirect in BusinessRoute. */

/**
 * In-content fallback for the per-page boundary below. Unlike the root
 * DefaultFallback (which replaces the whole viewport), this renders inside
 * <main> so the sidebar/header stay usable and the user can navigate away.
 */
const PageErrorFallback = ({ error, resetError }: { error: Error; resetError: () => void }) => {
  const { t } = useTranslation("dashboard");
  return (
    <div className="rounded-lg border border-border bg-card p-8 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
        <AlertTriangle className="h-6 w-6 text-destructive" />
      </div>
      <h2 className="text-lg font-semibold text-foreground">{t("pageError.title")}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{t("pageError.description")}</p>
      <pre className="mx-auto mt-4 max-h-32 max-w-lg overflow-auto rounded bg-muted p-3 text-left text-xs text-muted-foreground">
        {error.message}
      </pre>
      <Button className="mt-6 gap-2" onClick={resetError}>
        <RefreshCw className="h-4 w-4" />
        {t("pageError.retry")}
      </Button>
    </div>
  );
};

/**
 * Nested boundary around the routed page. Without it, any page throw bubbles
 * to the root boundary in main.tsx and replaces the entire shell. Keyed by
 * pathname so navigating to another page clears a caught error automatically.
 */
const PageBoundary = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  return (
    <ErrorBoundary
      key={location.pathname}
      fallback={(props) => <PageErrorFallback {...props} />}
    >
      {children}
    </ErrorBoundary>
  );
};

export const DashboardLayout = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  // The dashboard is already internationalized. Chrome (and other browsers)
  // still auto-translate when they see English job titles inside an otherwise
  // Spanish UI — they wrap text in <font> nodes, FullCalendar/React then
  // throw `removeChild`/`insertBefore` NotFoundError, and the page crashes.
  // Auth already opted its card out; do the same for the whole signed-in shell
  // so Calendar and every other panel page stay stable for every tenant.
  useEffect(() => {
    const el = document.documentElement;
    el.setAttribute("translate", "no");
    el.classList.add("notranslate");
    return () => {
      el.removeAttribute("translate");
      el.classList.remove("notranslate");
    };
  }, []);
  const { subscribed, bypass_subscription, loading: subLoading, refresh } = useSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const onboarding = useOnboardingCheck();
  const hasResolvedOnce = useRef(false);

  // After Stripe checkout returns, refresh subscription status
  useEffect(() => {
    if (searchParams.get('trial') !== 'started') return;
    searchParams.delete('trial');
    setSearchParams(searchParams, { replace: true });
    // Refresh subscription — webhook may need a moment to process. Keep the
    // retry timeout id so a remount can't stack overlapping refreshes.
    // force: bypass the backend's DB cache — the user just paid, and the
    // cached row can lag Stripe by up to 5 minutes.
    let retry: ReturnType<typeof setTimeout>;
    (async () => {
      await refresh({ force: true });
      retry = setTimeout(() => refresh({ force: true }), 3000);
    })();
    return () => clearTimeout(retry);
  }, []);

  // Show the full-screen spinner only on the FIRST resolve. Once we've rendered
  // the app, benign churn (a token refresh briefly re-flagging loading) must not
  // unmount <Outlet/> and remount the active page — that re-fired every page's
  // data queries.
  const stillLoading = subLoading || onboarding === "loading";
  if (!stillLoading) hasResolvedOnce.current = true;
  if (stillLoading && !hasResolvedOnce.current) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // New business — redirect to onboarding wizard before anything else, EXCEPT
  // on the billing page: a not-onboarded + unsubscribed user must be able to
  // reach billing to pay. Redirecting off billing here (while BusinessRoute
  // bounces them off /onboarding) is what froze the screen in an
  // /onboarding ↔ /dashboard/billing loop. Mirrors BusinessRoute's billing
  // exemption.
  const onBillingPage = location.pathname.endsWith('/billing');
  if (onboarding === "needs-onboarding" && !onBillingPage) {
    return <Navigate to="/onboarding" replace />;
  }

  // Hard paywall — no subscription and no bypass
  if (!subscribed && !bypass_subscription) {
    return <SubscriptionGuard><></></SubscriptionGuard>;
  }

  if (isMobile) {
    return (
      <div className="notranslate min-h-screen bg-background" translate="no">
        <div className="sticky top-0 z-40 flex items-center h-14 px-4 bg-card/80 backdrop-blur-xl border-b border-border">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("nav.openMenu")}
                className="text-muted-foreground hover:text-foreground hover:bg-secondary"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64 bg-background border-border">
              <Sidebar onNavigate={() => setOpen(false)} className="w-full h-full" />
            </SheetContent>
          </Sheet>
          <div className="flex items-center ml-3">
            <img src="/workloop-logo.png" alt="Workloop" width="24" height="24" decoding="async" className="h-6 w-6 mr-2" />
            <span className="text-lg font-bold text-white">Workloop</span>
          </div>
        </div>
        <main className="p-4">
          <Breadcrumbs />
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            <PageBoundary>
              <Outlet />
            </PageBoundary>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="notranslate min-h-screen bg-background" translate="no">
      <Sidebar />
      <div className="pl-64">
        <Header />
        <main className="p-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumbs />
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
              <PageBoundary>
                <Outlet />
              </PageBoundary>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
